import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Header } from '../components/ui/header';
import { Footer } from '../components/ui/footer';
import { useConversion } from '../hooks/useConversion';
import { SCORM_DOWNLOAD_PRICING } from '../lib/features';
import { HomeManual, StatsLedger, SUPPORT_EMAIL, useFaq } from './HomeManual';
import { icon } from '../components/ui/site-icon';
import { WaitlistForm } from '../components/ui/waitlist-form';
import { sizeMb, track } from '../utils/analytics';

// Converter hero and workflow screens; the homepage body below the hero is HomeManual. Styles live in src/styles/site.css.
const SEO_LANGS = ['en', 'uk', 'es', 'fr', 'pl', 'it', 'pt'];
const MAX_FILE_SIZE = 100 * 1024 * 1024;
const PPTX_TYPE = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';

export const ConverterPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const currentLang = lang || i18n.language || 'en';
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const faq = useFaq();
  const {
    isConverting,
    progress,
    currentStage,
    timeLeft,
    conversionResult,
    error,
    startConversion,
    handleDownload,
    handleNewConversion
  } = useConversion();

  const view = error ? 'error' : conversionResult ? 'success' : isConverting ? 'processing' : 'home';

  const resetConversion = () => {
    handleNewConversion();
    setFile(null);
    setFileError(null);
  };

  const { getRootProps, getInputProps, isDragActive, open: openFilePicker } = useDropzone({
    accept: { [PPTX_TYPE]: ['.pptx'] },
    multiple: false,
    noClick: true,
    noKeyboard: true,
    onDrop: (accepted, rejected) => {
      if (accepted.length + rejected.length > 1) {
        setFileError(t('site.converter.errorMultiple'));
        track('file_rejected', { reason: 'multiple' });
        return;
      }
      const candidate = accepted[0] ?? rejected[0]?.file;
      if (!candidate) return;
      if (!/\.pptx$/i.test(candidate.name)) {
        setFileError(t('site.converter.errorType'));
        track('file_rejected', { reason: 'type' });
        return;
      }
      if (candidate.size > MAX_FILE_SIZE) {
        setFileError(t('site.converter.errorSize'));
        track('file_rejected', { reason: 'size', file_size_mb: sizeMb(candidate.size) });
        return;
      }
      setFileError(null);
      setFile(candidate);
      // The step that decides the funnel: how many visitors get as far as a valid file.
      track('file_selected', { file_size_mb: sizeMb(candidate.size) });
    }
  });

  // Screen changes start at the top; returning home honours an anchor such as #pricing.
  useEffect(() => {
    const target = view === 'home' && window.location.hash ? document.getElementById(window.location.hash.slice(1)) : null;
    if (target) target.scrollIntoView();
    else window.scrollTo(0, 0);
  }, [view]);

  // Header anchors lead back to the landing from the result and error screens, as in the mockup.
  useEffect(() => {
    if (view !== 'success' && view !== 'error') return;
    window.addEventListener('hashchange', resetConversion);
    return () => window.removeEventListener('hashchange', resetConversion);
  }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConvert = async () => {
    if (!file) return;
    try {
      await startConversion(file, { resolution: 150, background: 'white', showSlideNumbers: true, format: 'scorm' });
    } catch (err) {
      console.error('Conversion error:', err);
    }
  };

  const breadcrumb = (label: string) => (
    <div className="breadcrumb">
      <a href={`/${currentLang}/`} onClick={(event) => { event.preventDefault(); resetConversion(); }}>Scormate</a>
      {icon('ChevronRight')}
      <span>{label}</span>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>{t('seo.title')}</title>
        <meta name="description" content={t('seo.description')} />
        <meta name="keywords" content={t('seo.keywords')} />
        <link rel="canonical" href={`https://scormate.com/${currentLang}/`} />
        {SEO_LANGS.map((code) => (
          <link key={code} rel="alternate" hrefLang={code} href={`https://scormate.com/${code}/`} />
        ))}
        <link rel="alternate" hrefLang="x-default" href="https://scormate.com/en/" />
        <meta property="og:title" content={t('seo.title')} />
        <meta property="og:description" content={t('seo.description')} />
        <meta property="og:locale" content={currentLang} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faq.map((item) => ({ '@type': 'Question', name: item.question, acceptedAnswer: { '@type': 'Answer', text: item.answer } }))
        })}</script>
      </Helmet>
      <Header />
      <main id="main" tabIndex={-1}>
        {view === 'home' && (
          <>
            <section className="hero" id="converter">
              <div className="wrap hero-grid">
                <div className="hero-copy">
                  <h1>{t('site.hero.titleLine1')} <span className="accent">{t('site.hero.titleLine2')}</span></h1>
                  <p className="lead">{t('site.hero.lead')}</p>
                  <ul className="hero-facts">
                    {(['factVersion', 'factFiles', 'factVideo'] as const).map((key) => <li key={key}>{icon('Check')}{t(`site.hero.${key}`)}</li>)}
                  </ul>
                  <p className="hero-bridge">
                    {t('site.hero.bridgeText')}{' '}
                    <a className="text-link" href="#ai-builder">{t('site.hero.bridgeLink')}{icon('ArrowRight')}</a>
                  </p>
                </div>

                <section className="converter-panel" aria-labelledby="converter-heading">
                  <div className="panel-bar"><span className="mono">POWERPOINT → SCORM 1.2</span><span className="tag">{t('site.converter.tag')}</span></div>
                  <div className="panel-body">
                    <h2 id="converter-heading">{t('site.converter.title')}</h2>
                    <p className="small muted">{t('site.converter.subtitle')}</p>
                    <div {...getRootProps({ className: `dropzone${isDragActive ? ' dragover' : ''}` })} hidden={Boolean(file)}>
                      <img className="upload-icon" src="/site/icons/Upload.svg" alt="" />
                      <p>{t('site.converter.drop')}</p>
                      <span className="small muted">{t('site.converter.orPick')}</span>
                      <button type="button" className="button primary" onClick={openFilePicker}>{t('site.converter.pick')}</button>
                      <span className="small muted">{t('site.converter.limit')}</span>
                    </div>
                    <input {...getInputProps()} aria-label={t('site.converter.inputLabel')} />
                    {fileError && <div className="alert danger" role="alert">{fileError}</div>}
                    {file && (
                      <>
                        <div className="selected-file">
                          {icon('FileText')}
                          <div>
                            <strong>{file.name}</strong>
                            <span className="small muted">{t('site.converter.size', { size: (file.size / 1024 / 1024).toFixed(1) })}</span>
                          </div>
                          <button type="button" className="icon-button" aria-label={t('site.converter.clear')} onClick={() => setFile(null)}>{icon('X')}</button>
                        </div>
                        <button type="button" className="button primary full" onClick={handleConvert}>{t('site.converter.convert')}</button>
                      </>
                    )}
                    <details className="file-details">
                      <summary>{t('site.converter.detailsSummary')}</summary>
                      <p>{t('site.converter.detailsText')}</p>
                    </details>
                  </div>
                  <div className="panel-foot">
                    <img src="/site/icons/ShieldCheck.svg" alt="" />
                    <Link className="text-button" to={`/${currentLang}/privacy-policy`}>{t('site.converter.privacy')}</Link>
                  </div>
                </section>
              </div>
            </section>
            <StatsLedger />
            <HomeManual currentLang={currentLang} />
          </>
        )}

        {view === 'processing' && (
          <section className="workflow-shell">
            <div className="workflow-container">
              {breadcrumb(t('site.processing.breadcrumb'))}
              <div className="workflow-card">
                <span className="eyebrow">{t('site.processing.eyebrow')}</span>
                <h1 style={{ fontSize: 28 }}>{t('site.processing.title')}</h1>
                {file && <p>{file.name}</p>}
                <div className="progress-stage" role="status">
                  <span>
                    {t(`converter.progress.${currentStage}`)}
                    {timeLeft ? ` · ${t('converter.progress.timeLeft', { time: timeLeft })}` : ''}
                  </span>
                  <div className="progress-track"><span style={{ width: `${Math.max(4, Math.min(100, progress))}%` }}></span></div>
                </div>
              </div>
            </div>
          </section>
        )}

        {view === 'success' && (
          <section className="workflow-shell">
            <div className="workflow-container">
              {breadcrumb(t('site.success.breadcrumb'))}
              <div className="workflow-title">
                <div>
                  <span className="status-label">{icon('CircleCheck')}{t('site.success.status')}</span>
                  <h1 style={{ marginTop: 12 }}>{t('site.success.title')}</h1>
                  <p>{t('site.success.text')}</p>
                </div>
              </div>
              <div className="success-grid">
                <section className="workflow-card">
                  <h2>{t('site.success.downloadTitle')}</h2>
                  <p>{t('site.success.downloadText')}</p>
                  <div className="selected-file">
                    {icon('FileText')}
                    <div>
                      <strong>{(file?.name ?? 'presentation.pptx').replace(/\.pptx$/i, '.zip')}</strong>
                      <span className="small muted">SCORM 1.2</span>
                    </div>
                  </div>
                  <button type="button" className="button secondary full" onClick={handleDownload}>{icon('Download')}{t('site.success.download')}</button>
                  <button type="button" className="text-button" onClick={resetConversion}>{t('site.success.another')}</button>
                </section>
                <section className="workflow-card ai-upsell">
                  <span className="eyebrow">{t('site.success.upsellEyebrow')}</span>
                  <h2>{t('site.success.upsellTitleLine1')}<br />{t('site.success.upsellTitleLine2')}</h2>
                  <p>{t('site.success.upsellText')}</p>
                  <div className="notice"><strong>{t('site.success.noticeTitle')}</strong><br />{t('site.success.noticeText')}</div>
                  <WaitlistForm source="conversion-success" currentLang={currentLang} />
                  <span className="small muted">{t(SCORM_DOWNLOAD_PRICING ? 'site.success.upsellNote' : 'site.success.upsellNoteFree')}</span>
                </section>
              </div>
            </div>
          </section>
        )}

        {view === 'error' && (
          <section className="workflow-shell">
            <div className="workflow-container">
              {breadcrumb(t('site.error.breadcrumb'))}
              <div className="workflow-card">
                <span className="eyebrow">{t('site.error.eyebrow')}</span>
                <h1 style={{ fontSize: 28 }}>{t('site.error.title')}</h1>
                <div className="alert danger" role="alert">{error}</div>
                <p>{t('site.error.advice')}</p>
                {file && (
                  <div className="selected-file">
                    {icon('FileText')}
                    <div><strong>{file.name}</strong><span className="small muted">{t('site.error.fileMeta')}</span></div>
                  </div>
                )}
                <button type="button" className="button primary" onClick={resetConversion}>{t('site.error.retry')}</button>
                <a className="text-link" href={SUPPORT_EMAIL}>{t('site.error.support')}</a>
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
};

